import { Heading, HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { FuzeLogo } from '@/components/Logo'
import { SUPPORT_EMAIL } from '@repo/constants'
import { Button, Separator } from '@repo/ui/components'
import { snappyEaseInOut } from '@repo/ui/lib/animations'
import { Link } from '@tanstack/react-router'
import { HomeIcon, RefreshCcwIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { ZodIssue } from 'zod'

type PageErrorProps = {
  status?: number
  message?: string
  issues?: ZodIssue[]
  global?: boolean
  offset?: boolean
}

/**
 * The error routes stash the URL the user was on in a `redirect` search param, so reloading the
 * error page itself would strand them there. Return to that URL instead when one is present.
 * Same-origin only: the value comes from the query string and must not become an open redirect.
 */
const getRedirectTarget = () => {
  const redirect = new URLSearchParams(window.location.search).get('redirect')
  if (!redirect) return null

  try {
    const target = new URL(redirect, window.location.origin)
    return target.origin === window.location.origin ? target.href : null
  } catch {
    return null
  }
}

const refreshPage = () => {
  const target = getRedirectTarget()
  if (target) window.location.replace(target)
  else window.location.reload()
}

const ErrorContent = (props: PageErrorProps) => {
  const { t } = useTranslation('common')

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: snappyEaseInOut }}
    >
      <VStack gap="xl" className={props.offset ? '-mt-56' : ''}>
        <FuzeLogo type="broken" className="text-foreground -ml-1 w-24" />
        <Heading level={1} leading="none" className="text-3xl">
          {t('errors.pageError.title')} <br />
          {t('errors.pageError.subTitle')}
        </Heading>

        <Separator />

        <VStack gap="lg">
          <VStack gap="md">
            <Heading level={3} leading="none">
              {t('errors.pageError.status', { status: props.status })}
            </Heading>

            <Text tone="muted" className="max-w-xl">
              <Text as="span">{props.message}</Text>
              <br />
              <br />
              {t('errors.pageError.description')}{' '}
              <Button variant="link" asChild className="h-auto p-0 leading-none">
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </Button>
            </Text>
          </VStack>

          <HStack gap="sm">
            <Button asChild size="sm">
              <Link to="/">
                <HomeIcon />
                {t('errors.pageError.goHome')}
              </Link>
            </Button>
            <Button variant="secondary" size="sm" onClick={refreshPage}>
              <RefreshCcwIcon />
              {t('errors.pageError.refreshPage')}
            </Button>
          </HStack>
        </VStack>
      </VStack>
    </motion.div>
  )
}

export function PageError(props: PageErrorProps) {
  if (props.global) {
    return (
      <div className="flex h-[100svh] items-center justify-center">
        <ErrorContent
          status={props.status}
          message={props.message}
          offset={props.offset || false}
        />
      </div>
    )
  }

  return (
    <PageContainer>
      <div className="flex h-[calc(100svh-173px)] items-center justify-center">
        <ErrorContent status={props.status} message={props.message} offset={props.offset || true} />
      </div>
    </PageContainer>
  )
}
